import React, { useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { WidgetTheme, TrendlineType, StackedDataPoint, TimezoneOption } from "../../types/analytics";
import { calculateTrendline, formatDateInTimezone } from "../../utils/analytics";

// Type workarounds for recharts components
const RESP: any = ResponsiveContainer as any;
const CC: any = ComposedChart as any;
const BR: any = Bar as any;
const LN: any = Line as any;
const XA: any = XAxis as any;
const YA: any = YAxis as any;
const CG: any = CartesianGrid as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;

/**
 * Color palette for stacked bar segments
 * Similar to the palette used in sticky filters
 */
const FOCUS_COLORS = [
    "#8884d8", // Purple
    "#82ca9d", // Green
    "#ffc658", // Yellow/Orange
    "#ff7c7c", // Red
    "#8b5cf6", // Violet
    "#06b6d4", // Cyan
    "#10b981", // Emerald
    "#f59e0b", // Amber
    "#ef4444", // Rose
    "#6366f1"  // Indigo
];

const OTHER_COLOR = "#9ca3af"; // Gray for "Other" category

// Re-export StackedDataPoint for backward compatibility
export type { StackedDataPoint };

type StackedBarChartProps = {
    data: StackedDataPoint[];
    focusField: string;
    focusValues: string[];
    theme: WidgetTheme;
    height?: number;
    showTrendline?: boolean;
    trendlineType?: TrendlineType;
    timezone?: TimezoneOption;
};

/**
 * Track which bars are hidden (toggled off) and which is being hovered
 */
type BarPropsState = {
    hover: string | null;
    [key: string]: boolean | string | null;
};

/**
 * Get display name for focus field
 */
const getFocusFieldDisplayName = (field: string): string => {
    const displayNames: Record<string, string> = {
        "attribution_id": "Attribution ID",
        "device_type": "Device Type",
        "brand_id": "Brand ID"
    };
    return displayNames[field] || field;
};

/**
 * Stacked Bar Chart component for visualizing grouped analytics data
 * Renders a recharts BarChart with dynamic Bar elements using stackId
 * 
 * Features:
 * - Click legend items to toggle visibility
 * - Hover legend items to highlight that bar segment
 */
const StackedBarChart: React.FC<StackedBarChartProps> = ({
    data,
    focusField,
    focusValues,
    theme,
    height = 400,
    showTrendline = false,
    trendlineType = TrendlineType.LINEAR,
    timezone = "local"
}) => {
    // Track which bars are hidden and which is being hovered
    const [barProps, setBarProps] = useState<BarPropsState>({ hover: null });

    // Format date key for display based on timezone
    const formatXAxisTick = (dateKey: string): string => {
        return formatDateInTimezone(dateKey, timezone, "short");
    };

    // Assign colors to focus values, with "Other" getting a gray color
    const getColorForValue = (value: string, index: number): string => {
        if (value === "Other") {
            return OTHER_COLOR;
        }
        return FOCUS_COLORS[index % FOCUS_COLORS.length];
    };

    // Ensure "Other" is always last in the order
    const orderedFocusValues = [...focusValues].sort((a, b) => {
        if (a === "Other") return 1;
        if (b === "Other") return -1;
        return 0;
    });

    // Calculate totals for each data point (only visible bars)
    const totals = data.map(point => {
        let sum = 0;
        orderedFocusValues.forEach(fv => {
            // Only include in total if bar is not hidden
            if (!barProps[fv]) {
                const val = point[fv];
                if (typeof val === "number") {
                    sum += val;
                }
            }
        });
        return sum;
    });

    // Calculate trendline values based on type
    const trendlineValues = calculateTrendline(totals, trendlineType);

    // Add trendline data to each point
    const dataWithTrend = data.map((point, i) => ({
        ...point,
        _trendTotal: totals[i],
        _trendline: trendlineValues[i]
    }));

    /**
     * Handle mouse entering a legend item - highlight that bar
     */
    const handleLegendMouseEnter = (dataKey: string): void => {
        if (!barProps[dataKey]) {
            setBarProps({ ...barProps, hover: dataKey });
        }
    };

    /**
     * Handle mouse leaving a legend item - remove highlight
     */
    const handleLegendMouseLeave = (): void => {
        setBarProps({ ...barProps, hover: null });
    };

    /**
     * Handle clicking a legend item - toggle bar visibility
     */
    const selectBar = (dataKey: string): void => {
        setBarProps({
            ...barProps,
            [dataKey]: !barProps[dataKey],
            hover: null
        });
    };

    return (
        <div style={{ 
            backgroundColor: theme.chartBg, 
            padding: "20px", 
            borderRadius: "5px",
            marginBottom: "20px"
        }}>
            {/* Interactive Legend */}
            <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "flex-start",
                marginBottom: "10px",
                flexWrap: "wrap",
                gap: "8px"
            }}>
                <div>
                    <span style={{ fontWeight: "bold", color: theme.text }}>
                        Grouped by: {getFocusFieldDisplayName(focusField)}
                    </span>
                    <div style={{ fontSize: "11px", color: theme.text, opacity: 0.6, marginTop: "2px" }}>
                        Click to toggle • Hover to highlight
                    </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {orderedFocusValues.map((value, index) => {
                        const isHidden = barProps[value] === true;
                        const isHovered = barProps.hover === value;
                        
                        return (
                            <div 
                                key={value} 
                                onClick={() => selectBar(value)}
                                onMouseEnter={() => handleLegendMouseEnter(value)}
                                onMouseLeave={handleLegendMouseLeave}
                                style={{ 
                                    display: "flex", 
                                    alignItems: "center", 
                                    gap: "4px",
                                    padding: "4px 8px",
                                    backgroundColor: isHovered ? "rgba(0,0,0,0.1)" : theme.background,
                                    borderRadius: "4px",
                                    border: `1px solid ${theme.border}`,
                                    cursor: "pointer",
                                    opacity: isHidden ? 0.4 : 1,
                                    transition: "all 0.15s ease",
                                    textDecoration: isHidden ? "line-through" : "none"
                                }}
                            >
                                <span 
                                    style={{ 
                                        width: "12px", 
                                        height: "12px", 
                                        backgroundColor: getColorForValue(value, index),
                                        borderRadius: "2px",
                                        opacity: isHidden ? 0.4 : 1
                                    }} 
                                />
                                <span style={{ 
                                    fontSize: "12px", 
                                    color: theme.text,
                                    fontWeight: isHovered ? "bold" : "normal"
                                }}>
                                    {value}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
            
            <RESP width="100%" height={height}>
                <CC data={dataWithTrend} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CG strokeDasharray="3 3" />
                    <XA dataKey="key" tickFormatter={formatXAxisTick} />
                    <YA />
                    <TT 
                        labelFormatter={formatXAxisTick}
                        contentStyle={{ 
                            backgroundColor: theme.background,
                            border: `1px solid ${theme.border}`,
                            color: theme.text
                        }}
                    />
                    <LG 
                        wrapperStyle={{ display: "none" }} 
                    />
                    {orderedFocusValues.map((value, index) => {
                        const isHidden = barProps[value] === true;
                        const isHovered = barProps.hover === value;
                        const hasHover = barProps.hover !== null;
                        
                        // Calculate opacity based on state
                        let opacity = 1;
                        if (isHidden) {
                            opacity = 0;
                        } else if (hasHover && !isHovered) {
                            opacity = 0.3; // Dim non-hovered bars
                        }
                        
                        return (
                            <BR
                                key={value}
                                dataKey={value}
                                stackId="focus"
                                fill={getColorForValue(value, index)}
                                name={value}
                                hide={isHidden}
                                fillOpacity={opacity}
                            />
                        );
                    })}
                    {showTrendline && (
                        <LN
                            type="monotone"
                            dataKey="_trendline"
                            stroke="#ff6b6b"
                            strokeWidth={2}
                            dot={false}
                            name="Trend"
                        />
                    )}
                </CC>
            </RESP>
        </div>
    );
};

export default StackedBarChart;
